import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatTree, MatTreeModule } from '@angular/material/tree';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  AssignedSkill,
  FLOW_GATEWAY,
  IWHBomNodeDTO,
  IWHBomNodeEndpointConfigModel,
  IWHBomNodeSkillDTO,
  WHAssetsENUM,
  WHBomNodeDataService,
  WHIconENUM,
  WHImgAuthHeaderModule,
  WHNgxDndDraggableModel,
} from '@workheld/workheld-shared-lib';
import {
  splitNameBySearchTerm,
  WHNestedBomNodeDOM,
} from 'src/app/w-h-module/w-h-bom-node-crud-form/w-h-nested-node.model';
import { environment } from 'src/environments/environment';
import { WHEnumDataService } from 'src/app/app-services-helper/w-h-enum-helper.service';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NodeMenuComponent } from './node-menu-actions.component';
import { EquipmentTreeStateService } from './equipment-tree-state.service';
import {
  catchError,
  debounceTime,
  first,
  firstValueFrom,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { WhDropzoneComponent } from 'src/app/app-shared/components/w-h-dropzone.component';
import { SkillDataService } from 'src/app/app-services-async/skill-data.service';
import { WHPipesModule } from 'src/app/app-shared/shared-pipes/wh-pipes.module';
import { dynamicAnimation } from 'src/app/app-shared/helpers/animations';
import { EquipmentStore } from 'src/app/app-modules-pages/equipment/equipment.store';
import { WHDndBehaviorSubjectHelperService } from 'src/app/app-services-helper/w-h-dnd-behavior-subject-helper.service';
import {
  BOM_NODE_SEARCH_MIN_TERM_LENGTH,
  BomNodeAsyncService,
  EMPTY_BOM_NODE_SEARCH_RESULT,
  IWHBomNodeSearchResultDTO,
  IWHBomNodeSearchTreeDTO,
} from 'src/app/app-services-async/bom-node-async.service';

/** Keystroke debounce before a search request is fired. */
const SEARCH_DEBOUNCE_MS = 400;

@Component({
  selector: 'app-equipment-structure',
  standalone: true,
  animations: [dynamicAnimation],
  styles: [
    `
      :host {
        flex: 1;
        display: grid;
      }

      .node-image {
        width: 24px;
        height: 24px;
        object-fit: cover;
        border-radius: 16px;
      }

      .skill-count {
        background-color: #e0e0e0;
        border: solid 2px var(--accent-color-500);
        min-width: 24px;
        min-height: 24px;
        border-radius: 50%;
        text-align: center;
        line-height: 1.7;
        display: inline-block;
      }

      .too-deep {
        min-width: calc(82vw + (var(--deep-level) - 45) * 24px);
        overflow: auto;
      }

      .too-deep-modal {
        min-width: calc(54vw + (var(--deep-level) - 25) * 24px);
        overflow: auto;
      }

      .overflow {
        white-space: normal;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 1;
        line-clamp: 1;
        -webkit-box-orient: vertical;
      }

      .mat-tree {
        width: 100%;
      }

      .w-48 {
        width: 48px;
      }
      .w-80 {
        padding-left: 4px;
        width: 80px;
      }
      .w-160 {
        width: 160px;
      }

      .decomissioned-node div:not(:has(.wh-status):not(.navigate-button)) {
        opacity: 0.7;
      }

      .selected-node {
        background-color: var(--primary-color-100) !important;
      }

      .equipment-structure-tree .mat-tree-node {
        min-height: 40px;
        align-items: center;
        position: relative;
        scrollbar-width: none;
        scroll-behavior: auto;
        -webkit-overflow-scrolling: auto;
        transition: 0.4s;

        &:has(.dnd-placeholder-box.active) {
          margin: 2px;
        }
      }

      .structure-node.clickable-node:hover {
        cursor: pointer;
      }

      /* A node without children offers no expand arrow (WHP-12434). Hidden
         rather than removed so names stay aligned across the level, and hidden
         rather than styled away so it is out of the tab and a11y order too. */
      .toggle-button.no-child-nodes {
        visibility: hidden;
      }

      /* Indent guide lines. The overlay is absolute, so it paints inside the
         padding matTreeNodePadding already reserves and cannot shift the row:
         one cell per indent step, the last one carrying the elbow. */
      .tree-guides {
        --tree-indent: 15px;
        --tree-guide-color: rgba(0, 0, 0, 0.14);
        --tree-elbow-radius: 6px;

        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        display: flex;
        pointer-events: none;
      }

      .tree-guide,
      .tree-elbow {
        position: relative;
        width: var(--tree-indent);
        flex: none;
      }

      /* Full-height vertical for an ancestor whose sibling list continues past
         this row. */
      .tree-guide.continues::before {
        content: '';
        position: absolute;
        left: 50%;
        top: 0;
        bottom: 0;
        border-left: 1px solid var(--tree-guide-color);
      }

      /* The elbow is one rounded corner — down the cell, then a curve out to the
         right — rather than two strokes meeting at a hard angle. It stops at the
         cell edge, leaving the toggle slot clear so the chevron is not crossed
         by a line. */
      .tree-elbow::before {
        content: '';
        position: absolute;
        left: 50%;
        right: 0;
        top: 0;
        height: 50%;
        border-left: 1px solid var(--tree-guide-color);
        border-bottom: 1px solid var(--tree-guide-color);
        border-bottom-left-radius: var(--tree-elbow-radius);
      }

      /* Not the last of its level, so the vertical carries on below the curve. */
      .tree-elbow:not(.last)::after {
        content: '';
        position: absolute;
        left: 50%;
        top: 50%;
        bottom: 0;
        border-left: 1px solid var(--tree-guide-color);
      }

      :host::ng-deep {
        .cdk-drag-placeholder {
          display: none !important;
        }
      }

      .equipment-structure-tree .mat-tree-node:nth-child(odd) {
        background-color: rgba(0, 0, 0, 0.02);
      }

      /* Sticky so the input stays reachable when the host scrolls the tree
         (the MN component's picker is a fixed-height scroll container). */
      .search-row {
        position: sticky;
        top: 0;
        z-index: 2;
        padding: 4px 8px;
        box-sizing: border-box;
        background-color: #fff;
        border-bottom: 1px solid #e8e8e8;
        width: 100%;
        flex-wrap: wrap;

        mat-form-field {
          max-width: 400px;
        }
      }

      .search-row .search-icon {
        margin-right: 8px;
        opacity: 0.6;
      }

      /* Pushed to the far end of the row, whether or not the search summary
         (which carries the flex-1) is currently rendered. */
      .search-row-actions {
        margin-left: auto;
      }

      .search-summary {
        white-space: nowrap;
        font-size: 12px;
        opacity: 0.7;
      }

      .search-summary.truncated {
        opacity: 1;
      }

      .search-empty {
        gap: 8px;
        opacity: 0.6;
      }

      /* A failed structure request, shown in place of the tree so an empty
         area is not mistaken for an equipment without children (WHP-12588). */
      .tree-load-error {
        gap: 8px;
      }

      /* A node that is only rendered to show the path down to a hit. */
      .search-context-node .structure-node-name {
        opacity: 0.6;
      }

      /* Highlighter-pen stroke behind the matching part of the name: the
         gradient stops leave the ascenders and the baseline uncovered, which is
         what makes it read as a marker rather than a filled box. */
      .search-marker {
        background: transparent
          linear-gradient(
            to bottom,
            transparent 12%,
            #ffe479 12%,
            #ffe479 92%,
            transparent 92%
          );
        color: inherit;
        font-weight: 500;
        padding: 0 1px;
        border-radius: 2px;
        box-decoration-break: clone;
        -webkit-box-decoration-break: clone;
      }
    `,
  ],
  imports: [
    CommonModule,
    MatTreeModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    NodeMenuComponent,
    WHImgAuthHeaderModule,
    WhDropzoneComponent,
    WHPipesModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
  ],
  template: `
    @if (enableSearch()) {
      @let searchTermValue = searchTermControl.value || '';
      <div class="search-row flex--r-c gap--8">
        <mat-form-field subscriptSizing="dynamic">
          <mat-icon matPrefix class="search-icon">search</mat-icon>
          <input
            matInput
            data-cy="bom-node-search-input"
            [formControl]="searchTermControl"
            [placeholder]="'app.search.label' | translate"
            autocomplete="off"
          />
          @if (searchTermValue) {
            <button
              mat-icon-button
              matSuffix
              type="button"
              data-cy="bom-node-search-clear"
              [matTooltip]="'app.ui.clearSelection.tooltip' | translate"
              (click)="clearSearch()"
            >
              <mat-icon>close</mat-icon>
            </button>
          }
          @if (searchTermValue.trim().length > 0 && !isSearchActive()) {
            <mat-hint align="end" data-cy="bom-node-search-min-length-hint">
              {{ searchTermValue.trim().length }} / {{ minSearchTermLength }}
            </mat-hint>
          }
        </mat-form-field>

        @if (isSearchActive() && searchResult(); as result) {
          <div
            class="search-summary flex-1 flex--r-c gap--4"
            [class.truncated]="result.truncated"
            data-cy="bom-node-search-summary"
            [matTooltip]="
              result.truncated
                ? result.returnedMatches + ' / ' + result.totalMatches
                : ''
            "
          >
            @if (result.truncated) {
              <mat-icon color="warn" style="font-size: 18px; height: 18px;"
                >warning</mat-icon
              >
              <span
                >{{ result.returnedMatches }} / {{ result.totalMatches }}</span
              >
            } @else {
              <span
                class="entry-count"
                [style.--numOfEntries]="result.totalMatches"
              ></span>
            }
          </div>
        }

        <!-- Host-supplied controls (the equipment detail page puts its
             decommissioned / node-id toggles here) share the search row instead
             of stacking above the tree. Only rendered while the search row is,
             so a host projecting them must also enable the search. -->
        <div class="search-row-actions flex--r-c flex--wrap gap--8">
          <ng-content select="[treeSearchActions]"></ng-content>
        </div>
      </div>
    }
    <div
      style="height: 44px; background-color: #fafafa"
      class="flex--row"
      [ngClass]="{
        'too-deep': deepestLevel() >= 45 && !isModalView(),
        'too-deep-modal': deepestLevel() >= 25 && isModalView(),
      }"
      [style.--deep-level]="deepestLevel()"
    >
      <div class="flex-1" style=" padding-left: 48px">
        {{ 'node.model.name' | translate }}
      </div>
      <div style="display: flex;">
        <div class="w-160">
          {{ 'node.model.type' | translate }}
        </div>
        @if (showInactiveChildren()) {
          <div class="w-80">
            {{ 'node.model.status' | translate }}
          </div>
        }
        <div *ngIf="hasSkillManagementEnabled()" class="w-48">
          {{ 'nav.skills.root' | translate }}
        </div>

        <div *ngIf="hasSkillManagementEnabled()" class="w-48"></div>
        @if (!isModalView()) {
          <div class="w-48"></div>
        }
      </div>
    </div>
    <w-h-dropzone
      [dropzoneId]="'wh-droplist-table'"
      [dropzoneType]="'tree'"
      (handleDrop)="onDrop($event)"
    >
      @if (isTreeBusy()) {
        <div class="flex--col w-100 m--8">
          @for (item of [].constructor(3); track $index; let i = $index) {
            <div
              class="flex--r-c w-100 p-l--32 m-t--8"
              style="box-sizing: border-box;"
            >
              <div class="skeleton-wrapper flex-3">
                <div
                  class="skeleton-items"
                  [ngStyle]="{ width: 74 + ((i * 17 + 9) % 23) + '%' }"
                >
                  <div></div>
                </div>
              </div>
              <div class="skeleton-wrapper flex-1">
                <div
                  class="skeleton-items"
                  [ngStyle]="{ width: 70 + ((i * 11 + 5) % 24) + '%' }"
                >
                  <div></div>
                </div>
              </div>
            </div>
          }
        </div>
      } @else if (hasTreeLoadFailed()) {
        <div
          class="tree-load-error flex--r-c-c p--16"
          data-cy="bom-node-tree-error"
        >
          <mat-icon color="warn">warning</mat-icon>
          <span>{{ 'app.somethingWentWrong.label' | translate }}</span>
          <button
            mat-icon-button
            type="button"
            color="accent"
            aria-label="Reload structure"
            data-cy="bom-node-tree-reload"
            (click)="reloadTree()"
          >
            <mat-icon>refresh</mat-icon>
          </button>
        </div>
      } @else if (isSearchActive() && searchResult()?.totalMatches === 0) {
        <div
          class="search-empty flex--r-c-c p--16"
          data-cy="bom-node-search-empty"
        >
          <mat-icon>search_off</mat-icon>
          <span>{{ 'app.nodata.label' | translate }}</span>
        </div>
      } @else {
        <mat-tree
          #tree
          [dataSource]="dataSource"
          [childrenAccessor]="childrenAccessor"
          [ngClass]="{
            'too-deep': deepestLevel() >= 45 && !isModalView(),
            'too-deep-modal': deepestLevel() >= 25 && isModalView(),
          }"
          [style.--deep-level]="deepestLevel()"
          style="overflow: hidden;"
          class="equipment-structure-tree"
        >
          <mat-tree-node
            *matTreeNodeDef="let node; when: hasChild"
            matTreeNodePadding
            matTreeNodePaddingIndent="15"
            [cdkTreeNodeTypeaheadLabel]="node.name"
            (expandedChange)="
              onToggle($event, node, nestedTreeNodeElement.ariaLevel)
            "
            data-cy="select-bomnode-row"
            (click)="onTreeNodeClick(node)"
            class="structure-node"
            [class.clickable-node]="isModalView()"
            [attr.data-node-id]="node.id"
            [style.--level]="nestedTreeNodeElement.ariaLevel - 1"
            [ngClass]="{
              'decomissioned-node': node?.entityStatus === 'INACTIVE',
              'selected-node': selectedBomNode?.id === node.id,
              'search-match-node': isSearchActive() && node?.searchMatch,
              'search-context-node': isSearchActive() && !node?.searchMatch,
            }"
            [attr.data-search-match]="
              isSearchActive() ? !!node?.searchMatch : null
            "
            #nestedTreeNodeElement
          >
            @let bomNodeStatus =
              node.id !== rootBomNodeId()
                ? BOM_NODE_STATUS[node?.bomNodeStatus]
                : EQUIPMENT_STATUS[node?.bomNodeStatus];
            @if (node.treeLevel > 0) {
              <div class="tree-guides" aria-hidden="true">
                @for (guide of node.guides; track $index) {
                  <span class="tree-guide" [class.continues]="guide"></span>
                }
                <span class="tree-elbow" [class.last]="node.isLastChild"></span>
              </div>
            }
            <div class="flex--r-c flex-4">
              <button
                mat-icon-button
                matTreeNodeToggle
                [attr.button-node-id]="node.id"
                [attr.aria-label]="'Toggle ' + node.name"
                class="toggle-button"
                [class.no-child-nodes]="!showToggle(node)"
                [disabled]="!showToggle(node)"
              >
                <mat-icon class="mat-icon-rtl-mirror" color="accent">
                  @if (node.isLoading()) {
                    <mat-spinner [diameter]="24"></mat-spinner>
                  } @else {
                    {{
                      tree.isExpanded(node) ? 'expand_more' : 'chevron_right'
                    }}
                  }
                </mat-icon>
              </button>

              <div class="flex--r-c gap--8 p--4">
                @if (node?.bomNodePicture?.downloadFile) {
                  <img
                    class="node-image"
                    alt="img"
                    *ngIf="
                      node?.bomNodePicture?.downloadFile
                        | WHImgAuthHeader
                        | async as image
                    "
                    [src]="image"
                  />
                } @else if (node.id === rootBomNodeId()) {
                  <img
                    class="node-image"
                    alt="img"
                    [src]="equipmentPlaceholderImg"
                  />
                } @else {
                  <i class="workheld-icons" [innerHtml]="bomIcon"></i>
                }
                @if (showNodeId()) {
                  <div>{{ node.extId }}</div>
                }
                <div
                  class="overflow structure-node-name"
                  matTooltip="{{ node.name }}"
                >
                  @if (node.nameSegments) {
                    @for (segment of node.nameSegments; track $index) {
                      @if (segment.isMatch) {
                        <mark class="search-marker">{{ segment.text }}</mark>
                      } @else {
                        <span>{{ segment.text }}</span>
                      }
                    }
                  } @else {
                    {{ node.name }}
                  }
                </div>
              </div>
              @if (node?.entityStatus === 'INACTIVE') {
                <div
                  [@dynamicAnimation]="{
                    value: 'right',
                    params: { blurAmount: '4px' },
                  }"
                  class="wh-status orange m-r--16"
                  style="opacity: 1 !important;"
                >
                  <span>{{ 'app.decommissioned.label' | translate }}</span>
                </div>
              }
            </div>

            <div class="flex--r-c">
              <div
                class="w-160 html-overflow"
                matTooltip="{{ BOM_NODE_TYPE[node?.type] }}"
              >
                {{ BOM_NODE_TYPE[node?.type] }}
              </div>

              @if (showInactiveChildren()) {
                <div
                  class="w-80 html-overflow"
                  matTooltip="{{
                    node?.bomNodeStatus ? (bomNodeStatus | titlecase) : '-'
                  }}"
                >
                  {{ node?.bomNodeStatus ? (bomNodeStatus | titlecase) : '-' }}
                </div>
              }

              @if (hasSkillManagementEnabled()) {
                <div
                  [id]="node.id"
                  class="dnd-placeholder-box workstep flex--r-c-c"
                  [ngClass]="{
                    active: draggable()?.data,
                  }"
                ></div>
                <div style="width: 48px">
                  @if (node?.skills?.length > 0) {
                    <span
                      class="skill-count"
                      [@dynamicAnimation]="{
                        value: 'left',
                        params: { blurAmount: '4px' },
                      }"
                    >
                      {{ node?.skills?.length }}
                    </span>
                  }
                </div>
              }
              @if (!isModalView()) {
                <div class="flex-1 flex--r-c-r">
                  <div style="min-width: 48px;">
                    <app-node-menu-action
                      [node]="node"
                      [rootBomNodeId]="rootBomNodeId()"
                      (nodeMenuClick)="onNodeMenuClick($event, node)"
                    ></app-node-menu-action>
                  </div>

                  <button
                    (click)="onNodeMenuClick('navigateTo', node, $event)"
                    [attr.data-cy]="'bom-node-navigate-button-' + node.id"
                    mat-icon-button
                    class="navigate-button"
                    style="opacity: 1 !important;"
                    [disabled]="node.id === rootBomNodeId() && !isModalView()"
                    color="accent"
                  >
                    <mat-icon>chevron_right</mat-icon>
                  </button>
                </div>
              }
            </div>
          </mat-tree-node>
        </mat-tree>
      }
    </w-h-dropzone>
    <div class="cl">&nbsp;</div>
  `,
})
export class EquipmentStructureComponent implements AfterViewInit {
  // ── Inputs ────────────────────────────────────────────────────────────────
  rootBomNodeId = input<string>(null);
  draggable = input<WHNgxDndDraggableModel>(null);
  resetSelectedNode = input<boolean>(false);
  includeDecommissioned = input<boolean>(false);
  showNodeId = input<boolean>(false);
  hasSkillManagementEnabled = input<boolean>(false);
  showSkillsColumn = input<boolean>(true);
  isModalView = input<boolean>(false);
  showInactiveChildren = input<boolean>(true);
  /**
   * Shows the free-text search above the tree (WHP-12435). Requires
   * `rootBomNodeId` to be a root BOM node — the search endpoint rejects
   * mid-tree ids.
   */
  enableSearch = input<boolean>(false);

  // ── Outputs ───────────────────────────────────────────────────────────────
  handleNodeMenuClick = output<{
    type: string;
    node: WHNestedBomNodeDOM;
    dataSource?: WHNestedBomNodeDOM[];
  }>();

  handleNodeMapChange = output<{ children: IWHBomNodeDTO[] }>();
  childrenAccessor = (node: any) => {
    if (!this.showInactiveChildren()) {
      return node.children.filter(
        (child: any) => child.bomNodeStatus !== 'INACTIVE',
      );
    }

    return node.children;
  };

  // ── View refs ─────────────────────────────────────────────────────────────
  @ViewChild('tree') tree: MatTree<any>;

  // ── Injected services ─────────────────────────────────────────────────────
  equipmentStore = inject(EquipmentStore);
  private readonly bomNodeDataService = inject(WHBomNodeDataService);
  private readonly bomNodeAsyncService = inject(BomNodeAsyncService);
  private readonly whEnumDataService = inject(WHEnumDataService);
  private readonly skillDataService = inject(SkillDataService);
  private readonly equipmentTreeStateService = inject(
    EquipmentTreeStateService,
  );
  private readonly dndBehaviorSubjectHelperService = inject(
    WHDndBehaviorSubjectHelperService,
  );
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly elementRef = inject(ElementRef);

  // ── Static assets & enums (template-facing) ───────────────────────────────
  readonly equipmentPlaceholderImg = WHAssetsENUM.equipmentPlaceholderPNG;
  readonly bomIcon = WHIconENUM.BomIcon;
  BOM_NODE_TYPE: Record<string, string> = {};
  BOM_NODE_STATUS: Record<string, string> = {};
  EQUIPMENT_STATUS: Record<string, string> = {};

  // ── Store shortcuts ───────────────────────────────────────────────────────
  refreshSkillCount = this.equipmentStore.shouldRefreshSkillCount;

  // ── Selection & data source ───────────────────────────────────────────────
  selectedBomNode: IWHBomNodeDTO;
  dataSource: WHNestedBomNodeDOM[] = [];
  /**
   * Browse tree kept aside while a search is active so clearing the input
   * restores it — including the nodes the user had already expanded.
   */
  private browseDataSource: WHNestedBomNodeDOM[] = [];

  // ── Tree expansion state ──────────────────────────────────────────────────
  expandedNodes = new Set<string>();
  private readonly expandedNodeLevels = new Map<string, number>();
  private readonly searchExpandedNodes = new Set<string>();
  private readonly searchExpandedNodeLevels = new Map<string, number>();
  deepestLevel = signal(0);
  private readonly previousRootBomNodeId = signal<string | null>(null);
  private lastIncludeDecommissioned = false;

  // Guard set while the search forest is being programmatically expanded, so
  // the expandedChange burst doesn't persist search-only state over the
  // browse tree's own expanded set.
  private readonly isExpandingSearchResults = signal(false);
  private expandSearchTimeoutId: ReturnType<typeof setTimeout> | null = null;

  // ── Search state (WHP-12435) ──────────────────────────────────────────────
  readonly minSearchTermLength = BOM_NODE_SEARCH_MIN_TERM_LENGTH;
  searchTermControl = new FormControl<string>('');
  /** Trimmed, debounced term the current result belongs to. */
  private readonly searchTerm = signal('');
  readonly isSearchActive = computed(
    () => this.searchTerm().length >= this.minSearchTermLength,
  );
  readonly searchResult = signal<IWHBomNodeSearchResultDTO | null>(null);
  private readonly isSearchLoading = signal(false);

  // ── Derived signals ───────────────────────────────────────────────────────
  isTreeBusy = computed(
    () =>
      this.equipmentStore.isTreeLoading() ||
      this.equipmentStore.isLoading() ||
      this.isSearchLoading(),
  );

  // ── Tree accessors ────────────────────────────────────────────────────────
  /**
   * The browse-tree request failed and left nothing to render (WHP-12588). An
   * empty tree area reads as "this equipment has no structure", so the error
   * gets its own state with a retry instead. A search renders its own forest
   * and stays visible even when the browse tree failed.
   */
  hasTreeLoadFailed = computed(
    () =>
      this.equipmentStore.treeLoadError() &&
      !this.equipmentStore.nodeTree() &&
      !this.isSearchActive(),
  );

  hasChild = (_: number, node: any) => true;

  /**
   * Search results are pruned, so a hit can arrive without its children —
   * `numChildNodes` still reflects the real tree (WHP-12434).
   */
  showToggle(node: WHNestedBomNodeDOM): boolean {
    return (
      node.children?.length > 0 ||
      (this.isSearchActive() && (node as any).numChildNodes > 0)
    );
  }

  constructor() {
    effect(() => {
      if (this.resetSelectedNode()) {
        this.selectedBomNode = null;
      }
    });

    // Clear tree state when equipment (rootBomNodeId) changes
    effect(() => {
      const currentRootId = this.rootBomNodeId();
      const previousRootId = this.previousRootBomNodeId();

      if (currentRootId !== previousRootId && previousRootId !== null) {
        // Equipment changed, reset tree state immediately
        this.dataSource = [];
        this.browseDataSource = [];
        this.expandedNodes.clear();
        this.expandedNodeLevels.clear();
        this.deepestLevel.set(0);
        untracked(() => this.resetSearch());
      }

      this.previousRootBomNodeId.set(currentRootId);
    });

    effect(() => {
      if (!this.rootBomNodeId()) {
        this.dataSource = [];
        this.browseDataSource = [];
      }
    });

    effect(() => {
      const currentNode = this.equipmentStore.currentNode();
      const nodeId = currentNode?.id;
      const includeDecommissioned = this.includeDecommissioned();
      if (nodeId) {
        // Skip if tree is already loaded for this node (and decommissioned didn't change)
        const nodeTree = untracked(() => this.equipmentStore.nodeTree());
        if (
          nodeTree?.id === nodeId &&
          this.lastIncludeDecommissioned === includeDecommissioned
        ) {
          return;
        }
        this.lastIncludeDecommissioned = includeDecommissioned;

        untracked(() => {
          this.equipmentStore.loadFullBomNodeTree({
            bomNodeId: nodeId,
            includeDecommissioned,
          });
        });
      } else {
        this.dataSource = [];
      }
    });

    effect(() => {
      const roots = this.equipmentStore.nodeTrees() ?? [];
      this.browseDataSource = roots;

      if (roots.length > 0) {
        untracked(() => {
          this.applyTreeRootGuides(roots);
          roots.forEach((root) => {
            this.applyTreeGuides(root, this.childrenAccessor(root));
          });
        });
      }

      // A search is rendering its own forest — keep the browse tree aside and
      // let clearSearch() put it back.
      if (untracked(() => this.isSearchActive())) {
        return;
      }

      this.dataSource = this.browseDataSource;

      if (roots.length > 0 && this.expandedNodes.size > 0) {
        this.restoreExpandedState();
      }
    });

    effect(() => {
      if (this.refreshSkillCount()) {
        this.refreshTreeWithNewSkillData();
      }
    });

    this.searchTermControl.valueChanges
      .pipe(
        map((value) => (value ?? '').trim()),
        debounceTime(SEARCH_DEBOUNCE_MS),
        tap((term) => {
          this.searchTerm.set(term);
          // Below the minimum term length there is nothing to search for —
          // fall back to the browse tree.
          if (term.length < this.minSearchTermLength) {
            this.exitSearchMode();
          }
        }),
        switchMap((term) => {
          if (term.length < this.minSearchTermLength) {
            return of(null);
          }

          const rootBomNodeId = this.rootBomNodeId();
          this.isSearchLoading.set(true);

          return this.bomNodeAsyncService
            .searchChildBomNodes(
              rootBomNodeId,
              term,
              this.includeDecommissioned(),
            )
            .pipe(
              map((result) => ({ rootBomNodeId, result })),
              catchError((error) => {
                console.error('Error searching bom nodes:', error);
                return of({
                  rootBomNodeId,
                  result: EMPTY_BOM_NODE_SEARCH_RESULT,
                });
              }),
            );
        }),
        takeUntilDestroyed(),
      )
      .subscribe((searchRequest) => {
        if (
          searchRequest !== null &&
          searchRequest.rootBomNodeId === this.rootBomNodeId() &&
          this.isSearchActive()
        ) {
          this.applySearchResult(searchRequest.result);
        }
      });

    this.BOM_NODE_TYPE = this.whEnumDataService.$ENUMS_OBJECT().BOM_NODE_TYPE;
    this.BOM_NODE_STATUS =
      this.whEnumDataService.$ENUMS_OBJECT().BOM_NODE_STATUS;
    this.EQUIPMENT_STATUS =
      this.whEnumDataService.$ENUMS_OBJECT().EQUIPMENT_STATUS;
  }

  ngAfterViewInit(): void {
    this.restoreExpandedState();
  }

  onDrop(event) {
    // Guard: skip if drag data was already consumed (prevents duplicate calls)
    const dndData = this.draggable()?.data;
    if (!dndData) return;

    let node = null;
    if (event.id) {
      node = this._findNodeById(event.id, this.dataSource);
      if (node) {
        // Clear drag data synchronously before async work to prevent re-entry
        this.draggable().data = undefined;
        this.dndBehaviorSubjectHelperService.dndBehaviorSubject.next(
          this.draggable(),
        );
        this.assignSkill(dndData.id, node);
      }
    }
  }

  onToggle(isExpanded: boolean, node: any, nodeLevel: number) {
    const isSearchActive = this.isSearchActive();
    const expandedNodes = isSearchActive
      ? this.searchExpandedNodes
      : this.expandedNodes;
    const expandedNodeLevels = isSearchActive
      ? this.searchExpandedNodeLevels
      : this.expandedNodeLevels;

    this.trackExpansionState(
      isExpanded,
      node,
      nodeLevel,
      expandedNodes,
      expandedNodeLevels,
    );

    if (isExpanded && isSearchActive && !node.children?.length) {
      this.hydrateSearchNode(node);
    }

    this.updateDeepestLevel(expandedNodeLevels);

    // Search expansion is transient — it must not overwrite the browse tree's
    // persisted expand state, which clearSearch() restores.
    if (!isSearchActive && !this.isExpandingSearchResults()) {
      this.equipmentTreeStateService.saveExpandedNodes(
        this.rootBomNodeId(),
        Array.from(this.expandedNodes),
      );
    }
  }

  private trackExpansionState(
    isExpanded: boolean,
    node: any,
    nodeLevel: number | null,
    expandedNodes: Set<string>,
    expandedNodeLevels: Map<string, number>,
  ): void {
    if (isExpanded) {
      expandedNodes.add(node.id);
      if (nodeLevel !== null) {
        expandedNodeLevels.set(node.id, nodeLevel);
      }
      return;
    }

    expandedNodes.delete(node.id);
    expandedNodeLevels.delete(node.id);
  }

  private updateDeepestLevel(expandedNodeLevels: Map<string, number>): void {
    this.deepestLevel.set(
      Math.max(0, ...Array.from(expandedNodeLevels.values())),
    );
  }

  onTreeNodeClick(node: WHNestedBomNodeDOM) {
    if (this.isModalView()) {
      this.selectedBomNode = node;
      this.handleNodeMenuClick.emit({
        type: '',
        node,
        dataSource: null,
      });
    }
  }

  /**
   * Re-requests the structure after a failed load (WHP-12588). The loading
   * effect only reacts to a changed node or toggle, so the retry has to ask
   * the store directly.
   */
  reloadTree() {
    const nodeId = this.equipmentStore.currentNode()?.id;
    if (!nodeId) {
      return;
    }

    this.equipmentStore.loadFullBomNodeTree({
      bomNodeId: nodeId,
      includeDecommissioned: this.includeDecommissioned(),
    });
  }

  /** Clears the input and puts the browse tree back (WHP-12435). */
  clearSearch() {
    this.searchTermControl.setValue('');
    this.searchTerm.set('');
    this.exitSearchMode();
  }

  /**
   * Renders the search forest. Every returned node is expanded so the matches
   * are visible without any clicking — showing a match inside its ancestor
   * chain is the whole point of the server-side search.
   */
  private applySearchResult(result: IWHBomNodeSearchResultDTO) {
    this.searchResult.set(result);
    this.searchExpandedNodes.clear();
    this.searchExpandedNodeLevels.clear();
    this.deepestLevel.set(0);
    this.selectedBomNode = null;
    this.dataSource = this.mapSearchTrees(result.trees, this.searchTerm());
    this.isSearchLoading.set(false);
    this.cdr.detectChanges();
    this.expandSearchResults();
  }

  /** Drops the search state and re-renders the browse tree. */
  private exitSearchMode() {
    if (this.searchResult() === null) {
      return;
    }

    this.searchResult.set(null);
    this.searchExpandedNodes.clear();
    this.searchExpandedNodeLevels.clear();
    this.selectedBomNode = null;
    this.dataSource = this.browseDataSource;
    this.isSearchLoading.set(false);
    this.deepestLevel.set(
      Math.max(0, ...Array.from(this.expandedNodeLevels.values())),
    );
    this.cdr.detectChanges();
    this.restoreExpandedState();
  }

  /** Resets input and state without touching the browse tree. */
  private resetSearch() {
    this.searchTermControl.setValue('', { emitEvent: false });
    this.searchTerm.set('');
    this.searchResult.set(null);
    this.searchExpandedNodes.clear();
    this.searchExpandedNodeLevels.clear();
    this.isSearchLoading.set(false);
  }

  private mapSearchTrees(
    trees: IWHBomNodeSearchTreeDTO[],
    term: string,
    level: number = 0,
    guides: boolean[] = [],
  ): WHNestedBomNodeDOM[] {
    const dtos = trees ?? [];
    return dtos.map((dto, index) => {
      // Strip the recursive children before construction — WHNestedBomNodeDOM
      // copies every property onto itself, so leaving them in would duplicate
      // the whole subtree.
      const { childNodes, ...flat } = dto;
      const node = new WHNestedBomNodeDOM(flat as unknown as IWHBomNodeDTO);
      node.nameSegments = splitNameBySearchTerm(dto.name, term);
      node.treeLevel = level;
      node.guides = guides;
      node.isLastChild = index === dtos.length - 1;
      node.children = this.mapSearchTrees(
        childNodes,
        term,
        level + 1,
        level > 0 ? [...guides, !node.isLastChild] : [],
      );
      return node;
    });
  }

  /**
   * Fills in the indent guide lines for a freshly built level.
   *
   * Rows render flat, so a node cannot see its ancestors at paint time — each
   * level inherits its parent's guide flags plus one for the parent itself,
   * which continues only while the parent still has a following sibling.
   * Computed over the *rendered* children, so a level whose tail is filtered out
   * by `childrenAccessor` still ends in a `└`.
   */
  private applyTreeGuides(
    parent: WHNestedBomNodeDOM | null,
    children: WHNestedBomNodeDOM[],
  ): void {
    const parentLevel = parent?.treeLevel ?? 0;
    // A top-level node has no indent step of its own, so it contributes no
    // guide to its children.
    const guides =
      parent && parentLevel > 0
        ? [...(parent.guides ?? []), !parent.isLastChild]
        : [];

    (children ?? []).forEach((child, index) => {
      if (!child) {
        return;
      }
      child.treeLevel = parentLevel + 1;
      child.guides = guides;
      child.isLastChild = index === children.length - 1;
      // The whole tree arrives at once, so every level needs its guides now.
      if (child.children?.length) {
        this.applyTreeGuides(child, this.childrenAccessor(child));
      }
    });
  }

  /** Marks a node as rendered at the top level, where no guides are drawn. */
  private applyTreeRootGuides(nodes: WHNestedBomNodeDOM[]): void {
    (nodes ?? []).forEach((node) => {
      if (!node) {
        return;
      }
      node.treeLevel = 0;
      node.guides = [];
      node.isLastChild = true;
    });
  }

  private expandSearchResults() {
    if (this.expandSearchTimeoutId !== null) {
      clearTimeout(this.expandSearchTimeoutId);
      this.expandSearchTimeoutId = null;
    }

    this.isExpandingSearchResults.set(true);
    // Deferred so #tree exists — it is only rendered once isTreeBusy() is false.
    this.expandSearchTimeoutId = setTimeout(() => {
      this.expandSearchTimeoutId = null;
      this.expandNodesRecursively(this.dataSource);
      this.cdr.detectChanges();
      this.scrollToFirstSearchMatch();
      // Delay reset so the expandedChange events fired by tree.expand() during
      // the next change detection cycle are still handled by the
      // short-circuit path in onToggle
      setTimeout(() => this.isExpandingSearchResults.set(false));
    });
  }

  private expandNodesRecursively(nodes: WHNestedBomNodeDOM[]) {
    if (!this.tree) return;

    for (const node of nodes) {
      if (!node.children?.length) continue;
      if (!this.tree.isExpanded(node)) {
        this.tree.expand(node);
      }
      this.expandNodesRecursively(node.children as WHNestedBomNodeDOM[]);
    }
  }

  /**
   * Brings the first hit into view. Everything is expanded, so the first match
   * can sit well below the fold of a deep ancestor chain — the user would
   * otherwise see nothing but context nodes.
   */
  private scrollToFirstSearchMatch() {
    const host = this.elementRef.nativeElement as HTMLElement;

    // Walk the hits in pre-order and take the first one that is actually
    // rendered — a hit under a filtered-out node has no row.
    let row: HTMLElement | null = null;
    for (const nodeId of this.collectSearchMatchIds(this.dataSource)) {
      row = host.querySelector<HTMLElement>(`[data-node-id="${nodeId}"]`);
      if (row) break;
    }
    if (!row) return;

    const container = this.findScrollableAncestor(row);
    if (!container) {
      row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      return;
    }

    const rowRect = row.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    // The search row is sticky inside the scroll container — keep its height
    // free above the match instead of scrolling the match underneath it.
    const stickyOffset =
      host.querySelector<HTMLElement>('.search-row')?.offsetHeight ?? 0;
    const offsetTop = rowRect.top - containerRect.top;

    const isFullyVisible =
      offsetTop >= stickyOffset && rowRect.bottom <= containerRect.bottom;
    if (isFullyVisible) return;

    container.scrollTo({
      top: container.scrollTop + offsetTop - stickyOffset,
      behavior: 'smooth',
    });
  }

  /** Node ids of every hit in the search forest, in depth-first pre-order. */
  private collectSearchMatchIds(
    nodes: WHNestedBomNodeDOM[],
    ids: string[] = [],
  ): string[] {
    for (const node of nodes) {
      if (node.searchMatch) {
        ids.push(node.id);
      }
      if (node.children?.length) {
        this.collectSearchMatchIds(node.children as WHNestedBomNodeDOM[], ids);
      }
    }
    return ids;
  }

  private findScrollableAncestor(element: HTMLElement): HTMLElement | null {
    let parent = element.parentElement;

    while (parent) {
      const overflowY = getComputedStyle(parent).overflowY;
      if (
        (overflowY === 'auto' || overflowY === 'scroll') &&
        parent.scrollHeight > parent.clientHeight
      ) {
        return parent;
      }
      parent = parent.parentElement;
    }

    return null;
  }

  onNodeMenuClick(type: string, node: WHNestedBomNodeDOM, event?) {
    if (event && type === 'navigateTo') {
      event.stopPropagation();
      this.equipmentStore.appendNavHistory(
        this.equipmentStore.currentNode().id,
      );
    }
    this.selectedBomNode = node;

    this.handleNodeMenuClick.emit({ type, node, dataSource: this.dataSource });
  }

  async refreshTreeWithNewSkillData(): Promise<void> {
    const refreshDto = this.refreshSkillCount();
    const expandedIds = new Set<string>(this.expandedNodes);

    if (refreshDto?.id) {
      expandedIds.add(refreshDto.id);
    }

    // If unassigning, also collect child node IDs
    if (refreshDto?.isUnassign) {
      const collectChildren = (node: WHNestedBomNodeDOM | undefined) => {
        if (!node?.children?.length) return;
        for (const child of node.children) {
          expandedIds.add(child.id);
          collectChildren(child);
        }
      };

      for (const nodeId of Array.from(expandedIds)) {
        const parentNode = this.findNodeInTree(this.dataSource, nodeId);
        collectChildren(parentNode);
      }
    }

    if (this.dataSource[0]?.id) {
      expandedIds.add(this.dataSource[0].id);
    }

    const allSkills: IWHBomNodeSkillDTO[] = [];
    const fetchPromises = Array.from(expandedIds).map(async (nodeId) => {
      const node = this.findNodeInTree(this.dataSource, nodeId);
      if (!node) return;

      let dto: IWHBomNodeDTO | null = null;

      try {
        if (refreshDto?.id === nodeId) {
          dto = refreshDto;
        } else {
          dto = await firstValueFrom(
            this.bomNodeDataService.getBomNodeDTO(
              {
                apiUrl: environment.apiUrl + FLOW_GATEWAY,
                bomNodeId: nodeId,
              },
              true,
            ),
          );
        }

        node.skills = dto.skills ?? [];
        node.entityStatus = dto.entityStatus;
        allSkills.push(...node.skills);
      } catch (error) {
        console.error(`Failed to refresh skills for node ${nodeId}`, error);
      }
    });

    await Promise.all(fetchPromises);

    const updated: AssignedSkill[] = [];

    allSkills.forEach((skill) => {
      const existing = updated.some((s) => s.id === skill.skillId);
      if (!existing) {
        updated.push({
          id: skill.skillId,
          name: skill.skillName,
          count: skill.count ?? 1,
          isOfBomNode: skill.isOfBomNode ?? false,
        } as AssignedSkill);
      }
    });

    this.skillDataService.setAssignedSkills(updated);
    this.cdr.detectChanges();
  }

  private _findNodeById(id: string, dataSource, removeChildren = false) {
    for (const node of dataSource) {
      if (node.id === id) {
        if (removeChildren && node.children) {
          node.children = []; // remove children
        }
        return node;
      }
      if (node.children) {
        const found = this._findNodeById(id, node.children, removeChildren);
        if (found) return found;
      }
    }
    return null;
  }

  private restoreExpandedState() {
    const savedNodes = this.equipmentTreeStateService.getExpandedNodes(
      this.rootBomNodeId(),
    );

    if (savedNodes.size === 0) return;

    this.expandedNodes = new Set(savedNodes);
    this.expandAllNodes(Array.from(savedNodes));
  }

  private expandAllNodes(nodeIds: string[]) {
    if (!this.tree) return;

    nodeIds.forEach((nodeId) => {
      const node = this.findNodeInTree(this.dataSource, nodeId);
      if (node && !this.tree.isExpanded(node)) {
        this.tree.expand(node);
      }
    });
  }

  /**
   * Fills a pruned search node with copies of its children from the already
   * loaded whole tree — copies, so search guides never touch browse rows.
   */
  private hydrateSearchNode(node: WHNestedBomNodeDOM): void {
    const browseNode = this.findNodeInTree(this.browseDataSource, node.id);
    if (!browseNode?.children?.length) return;

    const clone = (nodes: WHNestedBomNodeDOM[]): WHNestedBomNodeDOM[] =>
      nodes.map((child) =>
        Object.assign(new WHNestedBomNodeDOM(child as any), {
          children: clone(child.children as WHNestedBomNodeDOM[]),
        }),
      );
    node.children = clone(browseNode.children);
    this.applyTreeGuides(node, this.childrenAccessor(node));
    this.dataSource = [...this.dataSource];
  }

  private findNodeInTree(nodes: any[], nodeId: string): any {
    for (const node of nodes) {
      if (node.id === nodeId) return node;
      if (node.children) {
        const found = this.findNodeInTree(node.children, nodeId);
        if (found) return found;
      }
    }
    return null;
  }

  private assignSkill(skillId: string, node: WHNestedBomNodeDOM) {
    const config: IWHBomNodeEndpointConfigModel = {
      apiUrl: environment.apiUrl + FLOW_GATEWAY,
      bomNodeId: node.id,
      skillId: skillId,
    };

    // Blocks further skill drags from the side panel until the tree refresh
    // below settles, since it aggregates the counts shown on the skill chips.
    this.skillDataService.setSkillAssignmentInProgress(true);

    this.bomNodeDataService.assignSkillToBomNode(config).subscribe({
      next: () => {
        this.refreshNodeAndParents(node.id);
        this.onToggle(true, node, null);
      },
      error: () => {
        this.skillDataService.setSkillAssignmentInProgress(false);
      },
    });
  }

  private refreshNodeAndParents(nodeId: string): void {
    this.bomNodeDataService
      .getBomNodeDTO(
        {
          apiUrl: environment.apiUrl + FLOW_GATEWAY,
          bomNodeId: nodeId,
        },
        this.hasSkillManagementEnabled(),
      )
      .pipe(first())
      .subscribe({
        next: (dto: IWHBomNodeDTO) => {
          const localNode = this.findNodeInTree(this.dataSource, dto.id);
          if (localNode) {
            localNode.skills = dto.skills;
            this.cdr.detectChanges();
          }

          if (dto.parentNodeId) {
            this.refreshNodeAndParents(dto.parentNodeId);
          } else {
            // Reached the root node — update the chip list with the root's aggregated skill counts
            this.skillDataService.addSkillCountFromBomNode(dto.skills);
            this.skillDataService.setSkillAssignmentInProgress(false);
          }
        },
        error: () => {
          this.skillDataService.setSkillAssignmentInProgress(false);
        },
      });
  }
}
